/* ============================================================================
 * MTE - Banque de questions CE1 (Vague 2, Mission Rentree).
 * ----------------------------------------------------------------------------
 * 10 FR + 10 MA CE1, format QCM unique avec 1 bonne reponse.
 * Selection aleatoire par etape via selectQuestionsForStep.
 *
 * !! Ne PAS remplacer par les 180 items Lumen (canon) - ce banco est un
 * placeholder flow Vague 2 pour debloquer la navigation end-to-end.
 * ========================================================================== */

export type Matiere = 'FR' | 'MA';

export interface Question {
  id: string;
  matiere: Matiere;
  enonce: string;
  options: string[];
  bonneReponse: string;
  difficulte: 1 | 2 | 3;
  /** 1 ligne affichee en feedback. */
  explication: string;
}

export const QUESTIONS_FR_CE1: ReadonlyArray<Question> = [
  {
    id: 'FR-CE1-01',
    matiere: 'FR',
    enonce: 'Dans "fenetre", quel son entends-tu a la fin ?',
    options: ['[e]', '[E]', '[in]', '[on]'],
    bonneReponse: '[e]',
    difficulte: 1,
    explication: '"fenetre" se termine par le son [e] comme dans "ete".',
  },
  {
    id: 'FR-CE1-02',
    matiere: 'FR',
    enonce: "Conjugue : Ils ___ a l'ecole (present).",
    options: ['vont', 'va', 'allons', 'vais'],
    bonneReponse: 'vont',
    difficulte: 1,
    explication: "Avec ils, le verbe aller se conjugue vont.",
  },
  {
    id: 'FR-CE1-03',
    matiere: 'FR',
    enonce: 'Pluriel de "un cheval" :',
    options: ['des chevaux', 'des chevals', 'des chevales', 'des chevale'],
    bonneReponse: 'des chevaux',
    difficulte: 2,
    explication: '"cheval" fait "chevaux" au pluriel (irregulier).',
  },
  {
    id: 'FR-CE1-04',
    matiere: 'FR',
    enonce: 'Accorde : Les filles ___ contents (etre).',
    options: ['sont', 'est', 'es', 'son'],
    bonneReponse: 'sont',
    difficulte: 1,
    explication: 'Avec sujet pluriel "les filles", etre se conjugue "sont".',
  },
  {
    id: 'FR-CE1-05',
    matiere: 'FR',
    enonce: 'Choisis le mot feminin : une ___',
    options: ['mere', 'pere', 'frere', 'oncle'],
    bonneReponse: 'mere',
    difficulte: 1,
    explication: '"Mere" est du genre feminin.',
  },
  {
    id: 'FR-CE1-06',
    matiere: 'FR',
    enonce: 'Quelle phrase est correcte ?',
    options: [
      'Le chat mange la souris.',
      'Mange le chat la souris.',
      'La souris le chat mange.',
      'Le chat la mange souris.',
    ],
    bonneReponse: 'Le chat mange la souris.',
    difficulte: 2,
    explication: 'Ordre Sujet + Verbe + Complement = base phrase correcte.',
  },
  {
    id: 'FR-CE1-07',
    matiere: 'FR',
    enonce: 'Singulier de "des oiseaux" :',
    options: ['un oiseau', 'une oiseau', 'un oiseaux', 'des oiseau'],
    bonneReponse: 'un oiseau',
    difficulte: 2,
    explication: '"des oiseaux" devient "un oiseau" (des -> un).',
  },
  {
    id: 'FR-CE1-08',
    matiere: 'FR',
    enonce: 'Synonyme de "content" :',
    options: ['heureux', 'triste', 'grand', 'petit'],
    bonneReponse: 'heureux',
    difficulte: 1,
    explication: '"content" et "heureux" veulent dire la meme chose.',
  },
  {
    id: 'FR-CE1-09',
    matiere: 'FR',
    enonce: 'Infinitif de "Il mange" :',
    options: ['manger', 'mange', 'mangeons', 'mangent'],
    bonneReponse: 'manger',
    difficulte: 2,
    explication: '"mange" est la forme conjuguee de l\'infinitif "manger".',
  },
  {
    id: 'FR-CE1-10',
    matiere: 'FR',
    enonce: 'Lis : Le petit chat dort. Le chien joue. Combien d\'animaux ?',
    options: ['2', '3', '1', '4'],
    bonneReponse: '2',
    difficulte: 1,
    explication: 'Il y a 2 animaux : le chat et le chien.',
  },
];

export const QUESTIONS_MA_CE1: ReadonlyArray<Question> = [
  {
    id: 'MA-CE1-01',
    matiere: 'MA',
    enonce: 'Combien font 7 + 5 ?',
    options: ['12', '11', '13', '10'],
    bonneReponse: '12',
    difficulte: 1,
    explication: '7 + 5 = 12.',
  },
  {
    id: 'MA-CE1-02',
    matiere: 'MA',
    enonce: 'Combien font 12 - 8 ?',
    options: ['4', '3', '5', '2'],
    bonneReponse: '4',
    difficulte: 1,
    explication: '12 - 8 = 4.',
  },
  {
    id: 'MA-CE1-03',
    matiere: 'MA',
    enonce: 'Combien font 3 x 4 ?',
    options: ['12', '10', '7', '14'],
    bonneReponse: '12',
    difficulte: 2,
    explication: '3 x 4 = 3 + 3 + 3 + 3 = 12.',
  },
  {
    id: 'MA-CE1-04',
    matiere: 'MA',
    enonce: 'Complete : 23 ___ 32',
    options: ['<', '>', '=', 'aucune'],
    bonneReponse: '<',
    difficulte: 1,
    explication: '23 est plus petit que 32, donc 23 < 32.',
  },
  {
    id: 'MA-CE1-05',
    matiere: 'MA',
    enonce: '17 est-il pair ou impair ?',
    options: ['impair', 'pair', 'aucun', 'les deux'],
    bonneReponse: 'impair',
    difficulte: 1,
    explication: 'Un nombre impair ne peut pas etre partage en 2 groupes egaux.',
  },
  {
    id: 'MA-CE1-06',
    matiere: 'MA',
    enonce: 'Decompose : 47 =',
    options: ['40 + 7', '30 + 17', '50 - 3', '4 + 7'],
    bonneReponse: '40 + 7',
    difficulte: 2,
    explication: '47 = 4 dizaines + 7 unites = 40 + 7.',
  },
  {
    id: 'MA-CE1-07',
    matiere: 'MA',
    enonce: 'Quelle forme a 3 cotes ?',
    options: ['triangle', 'carre', 'cercle', 'rectangle'],
    bonneReponse: 'triangle',
    difficulte: 1,
    explication: 'Un triangle a exactement 3 cotes.',
  },
  {
    id: 'MA-CE1-08',
    matiere: 'MA',
    enonce: 'Classe du plus petit au plus grand : 5, 9, 3, 7, 1',
    options: ['1, 3, 5, 7, 9', '9, 7, 5, 3, 1', '1, 5, 3, 7, 9', '5, 1, 3, 7, 9'],
    bonneReponse: '1, 3, 5, 7, 9',
    difficulte: 2,
    explication: 'Ordre croissant : 1 < 3 < 5 < 7 < 9.',
  },
  {
    id: 'MA-CE1-09',
    matiere: 'MA',
    enonce: 'Combien : 1 EUR + 50 c + 20 c = ?',
    options: ['1 EUR 70', '1 EUR 50', '2 EUR 20', '1 EUR 20'],
    bonneReponse: '1 EUR 70',
    difficulte: 2,
    explication: '1 EUR + 50 c + 20 c = 1 EUR 70 centimes.',
  },
  {
    id: 'MA-CE1-10',
    matiere: 'MA',
    enonce: 'La moitie de 8 :',
    options: ['4', '6', '2', '16'],
    bonneReponse: '4',
    difficulte: 1,
    explication: 'La moitie de 8 est 8 / 2 = 4.',
  },
];

export const TOUTES_QUESTIONS_CE1: ReadonlyArray<Question> = [
  ...QUESTIONS_FR_CE1,
  ...QUESTIONS_MA_CE1,
];

export function getQuestionsByMatiere(matiere: Matiere): ReadonlyArray<Question> {
  return matiere === 'FR' ? QUESTIONS_FR_CE1 : QUESTIONS_MA_CE1;
}